import React from 'react'
import PropTypes from 'prop-types'
import ConnectionAvatar from './ConnectionAvatar'
import { CONNECTION_TYPES, getConnectionLabel } from '../../constants/connections'

/**
 * ConnectionsGrid Component
 *
 * Displays connections in a grid layout with grouping by type
 *
 * Props:
 * - connections: Grouped connections object {connected: [], minors: [], pending: [], tree_only: []}
 * - onConnectionClick: Handler when a connection is clicked
 * - showTreeOnly: Whether to show "Tree Only" connections (default: false)
 * - loading: Loading state
 */
function ConnectionsGrid({
  connections,
  onConnectionClick,
  showTreeOnly = false,
  loading = false
}) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-xl text-[#556B2F]">Loading connections...</div>
      </div>
    )
  }

  // Calculate totals
  const connectedCount = connections.connected?.length || 0
  const minorsCount = connections.minors?.length || 0
  const pendingCount = connections.pending?.length || 0
  const treeOnlyCount = connections.tree_only?.length || 0

  const hasConnections = connectedCount > 0 || minorsCount > 0 || pendingCount > 0

  if (!hasConnections && !showTreeOnly) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <div className="text-2xl mb-4">👨‍👩‍👧‍👦</div>
        <h3 className="text-xl font-bold text-gray-700 mb-2">No Connections Yet</h3>
        <p className="text-gray-600">Invite family members to start building your network!</p>
      </div>
    )
  }

  /**
   * Render a connection card
   */
  const renderConnectionCard = (connection) => {
    const { id, first_name, last_name, relationship, guardian_display, has_pending_activity } = connection

    return (
      <div
        key={id}
        className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer ${
          has_pending_activity ? 'bg-yellow-50 border-2 border-yellow-400' : ''
        }`}
        onClick={() => onConnectionClick && onConnectionClick(connection)}
      >
        {/* Avatar */}
        <div className="flex justify-center mb-3">
          <ConnectionAvatar connection={connection} size="lg" />
        </div>

        {/* Name */}
        <h3 className="text-center font-bold text-gray-900 text-lg mb-1">
          {first_name} {last_name}
        </h3>

        {/* Relationship */}
        {relationship && (
          <p className="text-center text-sm text-gray-600 mb-1">{relationship}</p>
        )}

        {/* Guardian info for minors */}
        {guardian_display && (
          <p className="text-center text-xs text-blue-600 italic">{guardian_display}</p>
        )}

        {/* Activity badge */}
        {has_pending_activity && (
          <div className="mt-3 text-center">
            <span className="inline-block px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
              New Activity
            </span>
          </div>
        )}
      </div>
    )
  }

  /**
   * Render a connection group section
   */
  const renderGroup = (title, connectionsArray, icon, bgColor) => {
    if (!connectionsArray || connectionsArray.length === 0) return null

    return (
      <div className="mb-8">
        {/* Section Header */}
        <div className={`flex items-center gap-2 mb-4 pb-2 border-b-2 ${bgColor}`}>
          <span className="text-2xl">{icon}</span>
          <h2 className="text-xl font-bold text-gray-800">
            {title}
            <span className="ml-2 text-sm font-normal text-gray-600">
              ({connectionsArray.length})
            </span>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {connectionsArray.map(renderConnectionCard)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Connected Adults */}
      {renderGroup(
        'Adults with Accounts',
        connections.connected,
        '✅',
        'border-green-500'
      )}

      {/* Minors */}
      {renderGroup(
        'Children (via Parents)',
        connections.minors,
        '👶',
        'border-blue-500'
      )}

      {/* Pending Invitations */}
      {renderGroup(
        'Pending Invitations',
        connections.pending,
        '⏳',
        'border-yellow-500'
      )}

      {/* Tree Only (optional) */}
      {showTreeOnly && renderGroup(
        'Tree Only',
        connections.tree_only,
        '🌳',
        'border-gray-500'
      )}
    </div>
  )
}

ConnectionsGrid.propTypes = {
  connections: PropTypes.shape({
    connected: PropTypes.array,
    minors: PropTypes.array,
    pending: PropTypes.array,
    tree_only: PropTypes.array
  }).isRequired,
  onConnectionClick: PropTypes.func,
  showTreeOnly: PropTypes.bool,
  loading: PropTypes.bool
}

export default ConnectionsGrid
